import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { fadeUp, softReveal, staggerParent } from "./motion";

const highlights = [
  { metric: "180%", label: "Avg. lead growth in 6 months" },
  { metric: "32+", label: "Active cross-industry campaigns" },
  { metric: "4.9/5", label: "Client satisfaction rating" },
];

const heroSignals = [
  { label: "SEO momentum", value: "+126%" },
  { label: "Qualified leads", value: "3.4x" },
  { label: "CAC efficiency", value: "-28%" },
];

const heroSteps = ["Attention", "Intent", "Conversion"];

const reasons = [
  {
    title: "Data-Driven Strategy",
    detail: "Every recommendation is tied to channel performance, buyer intent, and business goals.",
  },
  {
    title: "ROI Focused Execution",
    detail: "We optimize for qualified growth, not vanity metrics that look good but do not convert.",
  },
  {
    title: "Full-Funnel Support",
    detail: "From traffic generation to conversion flow and follow-up, the system works together.",
  },
  {
    title: "Transparent Reporting",
    detail: "You know what is improving, what is being tested, and what the next move is.",
  },
];

const results = [
  { metric: "120%+", label: "Traffic growth potential" },
  { metric: "3x", label: "Lead generation lift targets" },
  { metric: "40+", label: "Campaign and funnel workflows" },
];

const growthAreas = [
  {
    title: "SEO and Local Search",
    detail:
      "We improve technical foundations, on-page content, local visibility, and high-intent service pages so your business can show up when customers are already searching.",
  },
  {
    title: "Paid Ads and Lead Generation",
    detail:
      "We plan Google and Meta campaigns around clear conversion goals, stronger landing pages, and follow-up workflows that help turn ad spend into enquiries.",
  },
  {
    title: "Websites, Content, and Automation",
    detail:
      "We connect website experience, social content, CRM automation, and reporting so your digital marketing system keeps improving instead of running as disconnected tasks.",
  },
];

const focusMarkets = [
  "Clinics and hospitals",
  "Restaurants and food brands",
  "Local service businesses",
  "Real estate and property teams",
  "Growing SMBs",
  "Startup and founder-led brands",
];

const caseStudies = [
  {
    title: "Healthcare visibility system",
    detail:
      "Built local SEO, landing pages, social proof, and enquiry follow-up foundations for healthcare brands.",
    metric: "Healthcare",
  },
  {
    title: "Restaurant discovery engine",
    detail:
      "Improved local visibility, content rhythm, and campaign structure for food and restaurant businesses.",
    metric: "Food brands",
  },
  {
    title: "Lead response workflow",
    detail:
      "Connected web forms, WhatsApp intent, CRM tracking, and faster follow-up logic for service teams.",
    metric: "Automation",
  },
];

const authoritySignals = [
  "Hyderabad office presence",
  "Healthcare and local business focus",
  "SEO, ads, websites, CRM, and automation under one plan",
  "Structured reporting and weekly optimization rhythm",
];

const Home = () => {
  const navigate = useNavigate();

  return (
    <section className="home1">
      <div className="hero-shell">
        <div className="hero-grid">
          <div className="hero-copy">
            <motion.p className="hero-kicker" variants={fadeUp}>
              Digital Marketing Services for Growing Businesses
            </motion.p>
            <motion.h1 variants={fadeUp}>
              Grow Your Business Online with <span className="hero-accent">dYzital</span>
            </motion.h1>
            <motion.h2 variants={fadeUp}>
              We help businesses get more visibility, better leads, and stronger results from digital marketing.
            </motion.h2>

            <motion.p className="hero-description desktop-copy" variants={fadeUp}>
              From SEO and paid ads to content, websites, and follow-up systems, we build practical digital strategies that help businesses attract customers and turn interest into enquiries. Our office is in Madhapur, Hyderabad, and we support clients across service areas.
            </motion.p>
            <motion.p className="hero-description mobile-copy" variants={fadeUp}>
              We help businesses get found online, bring in better leads, and turn interest into enquiries.
            </motion.p>

            <motion.div className="hero-cta-row" variants={fadeUp}>
              <button className="cta-button cta-primary" onClick={() => navigate("/services")}>
                Explore Services
              </button>
              <button className="cta-button cta-outline" onClick={() => navigate("/contact")}>
                Book a Strategy Call
              </button>
            </motion.div>

            <motion.div className="hero-mini-proof" variants={fadeUp}>
              <span>More visibility</span>
              <span>Better leads</span>
              <span>Clear results</span>
            </motion.div>
          </div>

            <motion.aside className="hero-dashboard" variants={softReveal}>
              <div className="hero-dashboard-top">
              <p className="hero-dashboard-kicker">Growth System Overview</p>
              <span className="hero-dashboard-badge">Built for scale</span>
              </div>

            <div className="hero-chart-card">
              <div className="hero-chart-header">
                <strong>Marketing performance</strong>
                <span>Last 90 days</span>
              </div>
              <div className="hero-chart">
                <span className="hero-chart-bar hero-chart-bar-1" />
                <span className="hero-chart-bar hero-chart-bar-2" />
                <span className="hero-chart-bar hero-chart-bar-3" />
                <span className="hero-chart-bar hero-chart-bar-4" />
              </div>
            </div>

            <div className="hero-signal-grid">
              {heroSignals.map((signal) => (
                <div key={signal.label} className="hero-signal-card">
                  <span>{signal.label}</span>
                  <strong>{signal.value}</strong>
                </div>
              ))}
            </div>

            <div className="hero-flow-card">
              <p className="hero-dashboard-kicker">Customer journey</p>
              <div className="hero-flow-steps">
                {heroSteps.map((step) => (
                  <div key={step} className="hero-flow-step">
                    <span className="hero-flow-dot" />
                    <span>{step}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.aside>
        </div>
      </div>

      <motion.div className="hero-highlights" variants={staggerParent(0.18, 0.1)}>
        {highlights.map((item, index) => (
          <motion.article
            key={item.label}
            className="highlight-card"
            variants={softReveal}
            whileHover={{ y: -8, scale: 1.02 }}
            transition={{ duration: 0.22 }}
            custom={index}
          >
            <p className="highlight-metric">{item.metric}</p>
            <p className="highlight-label">{item.label}</p>
          </motion.article>
        ))}
      </motion.div>

      <motion.div className="home-proof-section" variants={staggerParent(0.14, 0.1)}>
        <motion.div className="home-proof-intro" variants={fadeUp}>
          <p className="section-kicker">Why Choose dYzital</p>
          <h2 className="desktop-copy">Built to help businesses grow online with clearer strategy, better execution, and stronger customer response.</h2>
          <h2 className="mobile-copy">Clear strategy. Better execution. Stronger response.</h2>
        </motion.div>

        <motion.div className="home-proof-grid" variants={staggerParent(0.18, 0.08)}>
          {reasons.map((reason) => (
            <motion.article key={reason.title} className="home-proof-card" variants={softReveal}>
              <h3>{reason.title}</h3>
              <p>{reason.detail}</p>
            </motion.article>
          ))}
        </motion.div>

        <motion.div className="home-results-band" variants={softReveal}>
          <div className="home-results-copy">
            <p className="section-kicker">Results Mindset</p>
            <h3>Digital marketing should bring real business results, not just numbers on a dashboard.</h3>
            <p>
              We focus on improving visibility, lead quality, and conversion performance for businesses across local, national, and online markets.
            </p>
          </div>

          <div className="home-results-grid">
            {results.map((item) => (
              <div key={item.label} className="home-result-card">
                <strong>{item.metric}</strong>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div className="home-seo-section" variants={staggerParent(0.14, 0.08)}>
          <motion.div className="home-seo-intro" variants={fadeUp}>
            <p className="section-kicker">Digital Marketing Support</p>
            <h2>Marketing systems for visibility, leads, and measurable business growth.</h2>
            <p className="desktop-copy">
              dYzital supports businesses that want practical digital marketing services instead of scattered activity. Our work covers search visibility, paid media, social content, website improvements, conversion tracking, CRM automation, and lead generation. The goal is simple: help more of the right people discover your business, understand your offer, and take the next step.
            </p>
            <p className="mobile-copy">
              Practical support for search, ads, content, websites, automation, and lead generation.
            </p>
          </motion.div>

          <div className="home-seo-grid">
            {growthAreas.map((area) => (
              <motion.article key={area.title} className="home-seo-card" variants={softReveal}>
                <h3>{area.title}</h3>
                <p>{area.detail}</p>
              </motion.article>
            ))}
          </div>

          <motion.div className="home-market-band" variants={softReveal}>
            <div>
              <p className="section-kicker">Who We Help</p>
              <h3>Built for local and growth-focused businesses.</h3>
              <p className="desktop-copy">
                We work especially well with teams that need clearer positioning, stronger local search presence, reliable lead flow, and transparent reporting across marketing channels.
              </p>
            </div>

            <div className="home-market-list">
              {focusMarkets.map((market) => (
                <span key={market}>{market}</span>
              ))}
            </div>
          </motion.div>
        </motion.div>

        <motion.div className="home-seo-section" variants={staggerParent(0.14, 0.08)}>
          <motion.div className="home-seo-intro" variants={fadeUp}>
            <p className="section-kicker">Proof and Authority</p>
            <h2>Trust signals for businesses that need more than a good-looking website.</h2>
            <p>
              We add strategy, execution discipline, reporting, and follow-up systems so marketing activity has a better chance of becoming qualified enquiries.
            </p>
          </motion.div>

          <div className="home-seo-grid">
            {caseStudies.map((item) => (
              <motion.article key={item.title} className="home-seo-card" variants={softReveal}>
                <p className="section-kicker">{item.metric}</p>
                <h3>{item.title}</h3>
                <p>{item.detail}</p>
                <button className="cta-button cta-outline" onClick={() => navigate("/contact")}>
                  Get Proposal
                </button>
              </motion.article>
            ))}
          </div>

          <motion.div className="home-market-band" variants={softReveal}>
            <div>
              <p className="section-kicker">Why It Works</p>
              <h3>Authority, local relevance, and conversion paths built into the same system.</h3>
            </div>

            <div className="home-market-list">
              {authoritySignals.map((signal) => (
                <span key={signal}>{signal}</span>
              ))}
            </div>
          </motion.div>

          <motion.div className="services-cta-band" variants={softReveal}>
            <h3>Want a search-ready growth plan for your business?</h3>
            <button className="cta-button cta-primary" onClick={() => navigate("/contact")}>
              Book Consultation
            </button>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Home;
